import React, { useState, useRef, useEffect, useCallback } from 'react';
import magnifyingGlass from '../../assets/images/magnifying-glass.svg';
import closeX from '../../assets/images/close-x.svg';
import styles from './SearchBar.module.scss';
import { Links } from '@/lib/constants';
import { useHistory } from 'react-router-dom';
import { RegisterThirdPartyChipModal } from './RegisterThirdPartyChipModal';
import { ReassignFiChipModal } from './ReassignFiChipModal';
import classNames from 'classnames';
import { PureQueryOptions } from '@apollo/client';
import { useAppViewContext } from '../lib/AppViewContext';
import { gqlTypes } from '@/types/gqlTypes';

export interface SearchBarProps {
  onQuerySubmitted(query: string): void;
  onQueryCleared(): void;
  query: string | null;
  searchAction?: string;
  hasOwnerInfo?: boolean;
  refetchQueries?: PureQueryOptions[];
}

const HIDE_ACTION_DELAY_MS = 200;
const DEBOUNCE_DELAY_MS = 300;

export default function SearchBar({
  onQuerySubmitted,
  onQueryCleared,
  query,
  searchAction,
  hasOwnerInfo = false,
  refetchQueries = [],
}: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputClearButtonRef = useRef<HTMLImageElement>(null);
  const history = useHistory();
  const { setSelectedStatus } = useAppViewContext();
  const [showRegisterThirdPartyChipModal, setShowRegisterThirdPartyChipModal] = useState(false);
  const [showReassignFiChipModal, setShowReassignFiChipModal] = useState(false);

  const handleReassignSuccess = (clearOwnerInfo: boolean) => {
    const targetStatus = clearOwnerInfo ? gqlTypes.ChipStatusType.SHIPPED : gqlTypes.ChipStatusType.ACTIVE;
    setSelectedStatus(targetStatus);
  };

  // Local value for immediate UI update, debounced before submitting to parent
  const [localValue, setLocalValue] = useState(query ?? '');

  // Sync local value when query prop changes (e.g. when cleared externally)
  useEffect(() => {
    setLocalValue(query ?? '');
  }, [query]);

  // Memoize callbacks to avoid re-triggering debounce effect
  const stableOnQuerySubmitted = useCallback(onQuerySubmitted, [onQuerySubmitted]);
  const stableOnQueryCleared = useCallback(onQueryCleared, [onQueryCleared]);

  // Debounce the search query submission
  useEffect(() => {
    if (localValue === '') {
      stableOnQueryCleared();
      return;
    }
    const timer = setTimeout(() => {
      stableOnQuerySubmitted(localValue);
    }, DEBOUNCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [localValue, stableOnQuerySubmitted, stableOnQueryCleared]);

  const value = localValue;

  const handleSearchClick = () => {
    inputRef.current?.classList.add(styles.visible);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.currentTarget.value);
  };

  const handleClearingSearch = () => {
    setLocalValue('');
    inputRef.current?.focus();
  };

  const [visibleAction, setVisibleAction] = useState<string>('');
  useEffect(() => {
    if (searchAction) {
      setVisibleAction(searchAction);
      return;
    }
    // delay hiding to avoid UI flicker when parent toggles during typing
    const t = setTimeout(() => setVisibleAction(''), HIDE_ACTION_DELAY_MS);
    return () => clearTimeout(t);
  }, [searchAction]);

  const showClear = value.length > 0 && !visibleAction;

  return (
    <>
      {showRegisterThirdPartyChipModal && (
        <RegisterThirdPartyChipModal
          serialNumber={value}
          onClose={(success = false) => {
            setShowRegisterThirdPartyChipModal(false);
            handleClearingSearch();
          }}
        />
      )}
      {showReassignFiChipModal && (
        <ReassignFiChipModal
          serialNumber={value}
          hasOwnerInfo={hasOwnerInfo}
          onClose={(success = false) => {
            setShowReassignFiChipModal(false);
            handleClearingSearch();
          }}
          onSuccess={handleReassignSuccess}
          refetchQueries={refetchQueries}
        />
      )}
      <form className={styles.searchForm} onSubmit={(e) => e.preventDefault()} onClick={handleSearchClick}>
        <img className={styles.searchIcon} src={magnifyingGlass} alt="Search your chips" />
        <input
          type="text"
          placeholder="Search by chip number or pet name"
          value={value}
          ref={inputRef}
          name="search"
          onChange={handleInputChange}
        />
        <img
          className={classNames({ [styles.visible]: showClear }, styles.closeIcon)}
          src={closeX}
          alt="Clear search"
          onClick={handleClearingSearch}
          ref={inputClearButtonRef}
        />
        {value.length > 0 && visibleAction === 'VIEW_CHIP' && (
          <button onClick={() => history.push(Links.search(value))}>View Chip</button>
        )}
        {value.length > 0 && visibleAction === 'REGISTER_NON_FI_CHIP' && (
          <button onClick={() => setShowRegisterThirdPartyChipModal(true)}>Register Non-Fi Chip</button>
        )}
        {value.length > 0 && visibleAction === 'REASSIGN_FI_CHIP' && (
          <button onClick={() => setShowReassignFiChipModal(true)}>Reassign Chip</button>
        )}
      </form>
    </>
  );
}
